import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Divider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useMemo } from 'react';

import type { FeatureDescriptor, FeatureMode } from '../featureOverrideSchema';
import { FEATURE_GROUPS, KNOWN_FEATURE_KEYS, modeForValue } from '../featureOverrideSchema';

export interface FeatureOverridesFormProps {
  /** The plan's own feature values, shown as the "default" each key falls back to. */
  planFeatures: Record<string, unknown>;
  modes: Record<string, FeatureMode>;
  numbers: Record<string, string>;
  onModeChange: (key: string, mode: FeatureMode) => void;
  onNumberChange: (key: string, raw: string) => void;
}

/** Render a plan default so an admin can see what "Default" actually gives. */
function describeDefault(value: unknown): string {
  if (value === undefined) return 'not set';
  if (value === null) return 'unlimited';
  if (typeof value === 'boolean') return value ? 'on' : 'off';
  return String(value);
}

function FeatureRow({
  descriptor,
  mode,
  numberValue,
  planValue,
  onModeChange,
  onNumberChange,
}: {
  descriptor: FeatureDescriptor;
  mode: FeatureMode;
  numberValue: string;
  planValue: unknown;
  onModeChange: (mode: FeatureMode) => void;
  onNumberChange: (raw: string) => void;
}): React.ReactElement {
  const isBoolean = descriptor.kind === 'boolean';
  const allowsUnlimited = descriptor.kind === 'nullableNumber';

  return (
    <Box sx={{ py: 1.25 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography variant="body2" fontWeight={600}>
              {descriptor.label}
            </Typography>
            {mode !== 'default' && <Chip size="small" label="overridden" color="warning" sx={{ height: 18 }} />}
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block">
            <code>{descriptor.key}</code> · plan default: {describeDefault(planValue)}
          </Typography>
          {descriptor.help && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
              {descriptor.help}
            </Typography>
          )}
        </Box>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={mode}
            onChange={(_event, next) => {
              // Null arrives when the active button is clicked again; keeping
              // the current mode is less surprising than clearing the row.
              if (next) onModeChange(next as FeatureMode);
            }}
            aria-label={`${descriptor.label} override mode`}
          >
            <ToggleButton value="default" aria-label={`${descriptor.label}: use plan default`}>
              Default
            </ToggleButton>
            {isBoolean ? (
              [
                <ToggleButton key="on" value="on" aria-label={`${descriptor.label}: on`}>
                  On
                </ToggleButton>,
                <ToggleButton key="off" value="off" aria-label={`${descriptor.label}: off`}>
                  Off
                </ToggleButton>,
              ]
            ) : (
              <ToggleButton value="value" aria-label={`${descriptor.label}: set a value`}>
                Set
              </ToggleButton>
            )}
            {allowsUnlimited && (
              <ToggleButton value="unlimited" aria-label={`${descriptor.label}: unlimited`}>
                Unlimited
              </ToggleButton>
            )}
          </ToggleButtonGroup>

          {!isBoolean && (
            <TextField
              size="small"
              type="number"
              value={numberValue}
              disabled={mode !== 'value'}
              onChange={(event) => onNumberChange(event.target.value)}
              inputProps={{ 'aria-label': `${descriptor.label} value` }}
              sx={{ width: 110 }}
            />
          )}
        </Stack>
      </Stack>
    </Box>
  );
}

/**
 * Key/value editor for `Subscription.featureOverrides`, replacing the raw JSON
 * textarea this drawer used to carry.
 *
 * Each key is a mode plus an optional number, never a free-text blob, because
 * the three states the server distinguishes — absent, null, and a value — are
 * not expressible in a single input. Any key stored on the subscription that
 * this form does not know about is surfaced read-only rather than silently
 * dropped on save.
 */
export function FeatureOverridesForm({
  modes,
  numbers,
  onModeChange,
  onNumberChange,
  planFeatures,
}: FeatureOverridesFormProps): React.ReactElement {
  const activeCount = useMemo(() => Object.values(modes).filter((mode) => mode !== 'default').length, [modes]);

  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {activeCount === 0
          ? 'No overrides — this account gets its plan’s features.'
          : `${activeCount} key${activeCount === 1 ? '' : 's'} overridden.`}
      </Typography>

      {FEATURE_GROUPS.map((group) =>
        group.legacy ? (
          <Accordion key={group.title} disableGutters elevation={0} sx={{ mt: 1, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
              <Typography variant="subtitle2">{group.title}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1, alignSelf: 'center' }}>
                dead reads — kept for historical plan rows
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0, pt: 0 }}>
              {group.features.map((descriptor) => (
                <FeatureRow
                  key={descriptor.key}
                  descriptor={descriptor}
                  mode={modes[descriptor.key] ?? 'default'}
                  numberValue={numbers[descriptor.key] ?? ''}
                  planValue={planFeatures[descriptor.key]}
                  onModeChange={(mode) => onModeChange(descriptor.key, mode)}
                  onNumberChange={(raw) => onNumberChange(descriptor.key, raw)}
                />
              ))}
            </AccordionDetails>
          </Accordion>
        ) : (
          <Box key={group.title} sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              {group.title}
            </Typography>
            <Divider />
            {group.features.map((descriptor) => (
              <FeatureRow
                key={descriptor.key}
                descriptor={descriptor}
                mode={modes[descriptor.key] ?? 'default'}
                numberValue={numbers[descriptor.key] ?? ''}
                planValue={planFeatures[descriptor.key]}
                onModeChange={(mode) => onModeChange(descriptor.key, mode)}
                onNumberChange={(raw) => onNumberChange(descriptor.key, raw)}
              />
            ))}
          </Box>
        ),
      )}
    </Box>
  );
}

/**
 * Keys stored on the subscription that the schema does not describe.
 *
 * Surfaced so a key added to `PlanFeatures` without a matching descriptor is
 * visible instead of quietly disappearing the next time someone saves.
 * @param overrides - The stored override object.
 */
export function unknownOverrideKeys(overrides: Record<string, unknown> | null): string[] {
  if (!overrides) return [];
  return Object.keys(overrides).filter((key) => !KNOWN_FEATURE_KEYS.has(key));
}

export { modeForValue };
